import {
  MultiselectTypeaheadInput,
  Option,
} from '@rsa-digital/evo-shared-components/components/Form/MultiselectTypeaheadInput';
import ListWithPagination from '@rsa-digital/evo-shared-components/components/ListWithPagination';
import { generateKeyForElement } from '@rsa-digital/evo-shared-components/helpers/elementKeyHelper';
import { graphql } from 'gatsby';
import React, { useState } from 'react';
import { RelatedContentCard } from 'components/blocks/RelatedContent';
import { RelatedContentCardData } from 'components/blocks/RelatedContent/types';
import { navigate } from 'helpers/navigation';
import { fixedHeaderHeight } from '../constants';

type RelatedContentCardListWithPaginationBlockProps = {
  related_content_card_list_with_pagination: {
    content_cards: RelatedContentCardData[];
    filter_options: Option[];
    filter_placeholder_text: string;
    items_to_display_count?: number;
  };
  currentPage?: number;
};

export const query = graphql`
  fragment RelatedContentCardListWithPaginationBlock on cs__related_content_card_list_with_pagination {
    content_cards {
      ...RelatedContentCardBlock
    }
    filter_options {
      label
    }
    filter_placeholder_text
    items_to_display_count
  }
`;

const itemColumnSpan = {
  desktop: 4,
  tabletLandscape: 4,
  tabletPortrait: 4,
  mobile: 4,
};

const RelatedContentCardListWithPaginationBlock: React.FC<
  RelatedContentCardListWithPaginationBlockProps
> = ({ related_content_card_list_with_pagination, currentPage }) => {
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<Option[]>([]);

  const filterCard = (relatedContentCard: RelatedContentCardData): boolean =>
    selectedFilterOptions
      .map((filter) => filter.label)
      .some(
        (filter) =>
          relatedContentCard.heading
            .toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()) ||
          relatedContentCard.subheading
            ?.toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase()) ||
          relatedContentCard.body
            ?.toLocaleLowerCase()
            .includes(filter.toLocaleLowerCase())
      );

  const itemsToDisplayCount =
    related_content_card_list_with_pagination.items_to_display_count || 9;
  const showFilter = related_content_card_list_with_pagination.filter_options?.length > 0;

  const onPageChange = (selectedPage: number): void => {
    if (currentPage === selectedPage) {
      return;
    }
    navigate('./', { state: { page: selectedPage }, replace: true });
  };

  return (
    <>
      {showFilter && (
        <MultiselectTypeaheadInput
          id={generateKeyForElement(related_content_card_list_with_pagination)}
          placeholder={related_content_card_list_with_pagination.filter_placeholder_text}
          options={related_content_card_list_with_pagination.filter_options}
          selectedOptions={selectedFilterOptions}
          setSelectedOptions={setSelectedFilterOptions}
        />
      )}
      <ListWithPagination
        itemsToDisplayCount={itemsToDisplayCount}
        initialPage={currentPage}
        onPageChange={onPageChange}
        displayDividers={false}
        gridItemProps={itemColumnSpan}
        fixedHeaderOffset={fixedHeaderHeight}
        enforceBottomSpaceConsistency
        customBottomSpacing={{
          tabletLandscape: 4,
          mobile: 3,
        }}
        data-cy="Related Content Card List with Pagination">
        {related_content_card_list_with_pagination.content_cards
          .filter((entry) => (selectedFilterOptions.length ? filterCard(entry) : true))
          .map((entry, index) => (
            <RelatedContentCard
              key={generateKeyForElement(entry)}
              data-cy={`List with Pagination Related Content Card ${index}`}
              related_content_card={{ ...entry }}
            />
          ))}
      </ListWithPagination>
    </>
  );
};

export default React.memo(RelatedContentCardListWithPaginationBlock);
